import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";

export type AnnotationTool = "pen" | "highlighter" | "text" | "arrow" | "circle" | "rectangle";

interface Props {
  currentPage: number;
  totalPages: number;
  scale: number;
  rotation: number;
  flipped: boolean;
  brushColor: string;
  lineWidth: number;
  canUndo: boolean;
  canRedo: boolean;
  activeTool: AnnotationTool;
  onPageChange: (page: number) => void;
  onScaleChange: (scale: number) => void;
  onRotationChange: (rotation: number) => void;
  onFlip: () => void;
  onColorChange: (color: string) => void;
  onLineWidthChange: (width: number) => void;
  onUndo: () => void;
  onRedo: () => void;
  onClear: () => void;
  onToolChange: (tool: AnnotationTool) => void;
}

const colors = [
  { value: "#FF0000", label: "Red" },
  { value: "#008000", label: "Green" },
  { value: "#0000FF", label: "Blue" },
  { value: "#000000", label: "Black" },
];

const scales = [0.5, 1, 1.5, 2, 2.5];
const rotations = [0, 90, 180, 270];

const AnnotationToolbar: React.FC<Props> = ({
  currentPage,
  totalPages,
  scale,
  rotation,
  flipped,
  brushColor,
  lineWidth,
  canUndo,
  canRedo,
  activeTool,
  onPageChange,
  onScaleChange,
  onRotationChange,
  onFlip,
  onColorChange,
  onLineWidthChange,
  onUndo,
  onRedo,
  onClear,
  onToolChange,
}) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        alignItems: "center",
        padding: "8px 12px",
        background: "var(--surface-page)",
        borderBottom: "1px solid var(--border-default)",
        fontSize: "0.85em",
        position: "relative",
      }}
    >
      {/* Page navigation */}
      <Button
        variant="secondary"
        size="sm"
        disabled={currentPage <= 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        &laquo;
      </Button>
      <Form.Select
        size="sm"
        style={{ width: "80px" }}
        value={currentPage}
        onChange={(e) => onPageChange(Number(e.target.value))}
      >
        {Array.from({ length: totalPages }, (_, i) => (
          <option key={i + 1} value={i + 1}>
            {i + 1}/{totalPages}
          </option>
        ))}
      </Form.Select>
      <Button
        variant="secondary"
        size="sm"
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        &raquo;
      </Button>

      <span style={{ borderLeft: "1px solid var(--border-strong)", height: "24px" }} />

      {/* Zoom */}
      <Form.Select
        size="sm"
        style={{ width: "75px" }}
        value={scale}
        onChange={(e) => onScaleChange(Number(e.target.value))}
      >
        {scales.map((s) => (
          <option key={s} value={s}>
            {s}x
          </option>
        ))}
      </Form.Select>

      {/* Rotation */}
      <Form.Select
        size="sm"
        style={{ width: "75px" }}
        value={rotation}
        onChange={(e) => onRotationChange(Number(e.target.value))}
      >
        {rotations.map((r) => (
          <option key={r} value={r}>
            {r}&deg;
          </option>
        ))}
      </Form.Select>

      {/* Horizontal Flip */}
      <Button
        variant={flipped ? "primary" : "secondary"}
        size="sm"
        onClick={onFlip}
        title="Flip Horizontally"
      >
        Flip
      </Button>

      <span style={{ borderLeft: "1px solid var(--border-strong)", height: "24px" }} />

      {/* Color picker */}
      {colors.map((c) => (
        <span
          key={c.value}
          title={c.label}
          onClick={() => onColorChange(c.value)}
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            background: c.value,
            cursor: "pointer",
            border:
              brushColor === c.value
                ? "3px solid #ffd700"
                : "2px solid #ccc",
            display: "inline-block",
          }}
        />
      ))}

      {/* Line width */}
      <Form.Select
        size="sm"
        style={{ width: "65px" }}
        value={lineWidth}
        onChange={(e) => onLineWidthChange(Number(e.target.value))}
      >
        {[1, 2, 3, 4, 5].map((w) => (
          <option key={w} value={w}>
            {w}px
          </option>
        ))}
      </Form.Select>

      <span style={{ borderLeft: "1px solid var(--border-strong)", height: "24px" }} />

      {/* Drawing Tools */}
      <button
        onClick={() => onToolChange("pen")}
        style={{
          background: activeTool === "pen" ? "#337ab7" : "#6c757d",
          color: "var(--text-on-brand)",
          border: "none",
          padding: "4px 12px",
          borderRadius: "4px",
          cursor: "pointer",
          fontSize: "0.85em",
          fontWeight: 500,
        }}
        title="Pen Tool"
      >
        ✏️ Pen
      </button>
      <button
        onClick={() => onToolChange("highlighter")}
        style={{
          background: activeTool === "highlighter" ? "#337ab7" : "#6c757d",
          color: "var(--text-on-brand)",
          border: "none",
          padding: "4px 12px",
          borderRadius: "4px",
          cursor: "pointer",
          fontSize: "0.85em",
          fontWeight: 500,
        }}
        title="Highlighter"
      >
        🖍️ Highlight
      </button>

      <span style={{ borderLeft: "1px solid var(--border-strong)", height: "24px" }} />

      {/* Undo / Redo */}
      <Button
        variant="secondary"
        size="sm"
        disabled={!canUndo}
        onClick={onUndo}
        title="Undo (Ctrl+Z)"
      >
        Undo
      </Button>
      <Button
        variant="secondary"
        size="sm"
        disabled={!canRedo}
        onClick={onRedo}
        title="Redo (Ctrl+Y)"
      >
        Redo
      </Button>

      <span style={{ borderLeft: "1px solid var(--border-strong)", height: "24px" }} />

      {/* Clear Page Writing */}
      <button
        onClick={onClear}
        style={{
          background: "var(--status-danger)",
          color: "var(--text-on-brand)",
          border: "none",
          padding: "4px 12px",
          borderRadius: "4px",
          cursor: "pointer",
          fontSize: "0.85em",
          fontWeight: 500,
        }}
      >
        Clear Page Writing
      </button>

      <span style={{ borderLeft: "1px solid var(--border-strong)", height: "24px" }} />

      {/* More Tools Menu */}
      <div style={{ position: "relative" }}>
        <button
          onClick={() => setShowMenu(!showMenu)}
          style={{
            background: "var(--status-success)",
            color: "var(--text-on-brand)",
            border: "none",
            padding: "4px 12px",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "0.85em",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
          title="More Annotation Tools"
        >
          🛠️ Tools
        </button>

        {showMenu && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              right: 0,
              marginTop: "4px",
              background: "var(--surface-card)",
              border: "1px solid var(--border-strong)",
              borderRadius: "4px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              zIndex: 1000,
              minWidth: "180px",
            }}
          >
            <button
              onClick={() => {
                onToolChange("text");
                setShowMenu(false);
              }}
              style={{
                width: "100%",
                background: activeTool === "text" ? "#e7f3ff" : "#fff",
                border: "none",
                padding: "8px 12px",
                textAlign: "left",
                cursor: "pointer",
                fontSize: "0.9em",
                borderBottom: "1px solid var(--border-subtle)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-sunken)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = activeTool === "text" ? "#e7f3ff" : "#fff")}
            >
              📝 Text Box
            </button>
            <button
              onClick={() => {
                onToolChange("arrow");
                setShowMenu(false);
              }}
              style={{
                width: "100%",
                background: activeTool === "arrow" ? "#e7f3ff" : "#fff",
                border: "none",
                padding: "8px 12px",
                textAlign: "left",
                cursor: "pointer",
                fontSize: "0.9em",
                borderBottom: "1px solid var(--border-subtle)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-sunken)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = activeTool === "arrow" ? "#e7f3ff" : "#fff")}
            >
              ➤ Arrow
            </button>
            <button
              onClick={() => {
                onToolChange("circle");
                setShowMenu(false);
              }}
              style={{
                width: "100%",
                background: activeTool === "circle" ? "#e7f3ff" : "#fff",
                border: "none",
                padding: "8px 12px",
                textAlign: "left",
                cursor: "pointer",
                fontSize: "0.9em",
                borderBottom: "1px solid var(--border-subtle)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-sunken)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = activeTool === "circle" ? "#e7f3ff" : "#fff")}
            >
              ⭕ Circle
            </button>
            <button
              onClick={() => {
                onToolChange("rectangle");
                setShowMenu(false);
              }}
              style={{
                width: "100%",
                background: activeTool === "rectangle" ? "#e7f3ff" : "#fff",
                border: "none",
                padding: "8px 12px",
                textAlign: "left",
                cursor: "pointer",
                fontSize: "0.9em",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-sunken)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = activeTool === "rectangle" ? "#e7f3ff" : "#fff")}
            >
              ▭ Rectangle
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnotationToolbar;
